/**
 * Native face capture for the FAST Kiosk pipeline (Capacitor + ML Kit).
 *
 * Flow (on a real Android/iOS device only):
 *   native camera (front) → base64 JPEG → ML Kit on-device face detection +
 *   attribute classification (eye-open probabilities, head Euler angles) →
 *   instant liveness verdict → photo + signals sent to backend identify-v2.
 *
 * On the WEB (preview / desktop) the ML Kit plugin is unavailable, so
 * `isNativeFaceAvailable()` returns false and the kiosk transparently uses the
 * existing browser faceapi.js pipeline (FaceCaptureView).
 */
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

const LIVENESS_THRESHOLDS = {
  eyeOpen: 0.5, // both eyes must be >= 50% open
  yawDegrees: 25, // head within 25° left/right
  pitchDegrees: 25, // head within 25° up/down
};

// Lazily import the ML Kit plugin so the web bundle never hard-depends on a
// native-only module at import time.
const _getFaceDetection = async () => {
  const mod = await import('@capacitor-mlkit/face-detection');
  return mod.FaceDetection;
};

/** True only on a native platform where ML Kit can run. */
export const isNativeFaceAvailable = async () => {
  try {
    if (!Capacitor.isNativePlatform()) return false;
    const FaceDetection = await _getFaceDetection();
    // Some plugin versions expose isAvailable(); tolerate its absence.
    if (typeof FaceDetection.isAvailable === 'function') {
      const res = await FaceDetection.isAvailable();
      return res?.available !== false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Capture a photo and run ML Kit detection + liveness.
 * Returns:
 *   { success:false, reason:'no_face_detected' | 'cancelled' }
 *   { success:true, photo_base64, bbox, liveness_passed, liveness_attributes, face_count }
 */
export const captureAndDetect = async () => {
  let photo;
  try {
    photo = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera,
      direction: 'front',
      width: 640,
      height: 640,
    });
  } catch {
    return { success: false, reason: 'cancelled' };
  }

  const FaceDetection = await _getFaceDetection();
  const detectionResult = await FaceDetection.processImage
    ? await FaceDetection.processImage({
        path: undefined,
        base64String: photo.base64String,
        performanceMode: 'fast',
        landmarkMode: 'none',
        contourMode: 'none',
        classificationMode: 'all',
      }).catch(() => null)
    : null;

  // Fallback to the documented `detect` signature when `processImage` is absent.
  const result = detectionResult || await FaceDetection.detect({
    image: { base64String: photo.base64String },
    performanceMode: 'fast',
    landmarkMode: 'none',
    contourMode: 'none',
    classificationMode: 'all',
  }).catch(() => null);

  const detected = result?.faces || [];
  if (!detected.length) {
    return { success: false, reason: 'no_face_detected', photo_base64: photo.base64String };
  }

  const frameArea = (f) => {
    const fr = f.frame || f.bounds || {};
    return (fr.width || 0) * (fr.height || 0);
  };
  const face = detected.reduce((a, b) => (frameArea(a) >= frameArea(b) ? a : b));

  const leftEyeOpen = face.leftEyeOpenProbability ?? 1.0;
  const rightEyeOpen = face.rightEyeOpenProbability ?? 1.0;
  const headYaw = face.headEulerAngleY ?? 0;
  const headPitch = face.headEulerAngleX ?? 0;

  const liveness_attributes = {
    left_eye_open: leftEyeOpen,
    right_eye_open: rightEyeOpen,
    head_yaw: headYaw,
    head_pitch: headPitch,
  };
  const liveness_passed = (
    leftEyeOpen >= LIVENESS_THRESHOLDS.eyeOpen &&
    rightEyeOpen >= LIVENESS_THRESHOLDS.eyeOpen &&
    Math.abs(headYaw) <= LIVENESS_THRESHOLDS.yawDegrees &&
    Math.abs(headPitch) <= LIVENESS_THRESHOLDS.pitchDegrees
  );

  return {
    success: true,
    photo_base64: photo.base64String,
    bbox: face.frame || face.bounds || null,
    liveness_passed,
    liveness_attributes,
    face_count: detected.length,
  };
};
